import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import CurrentParticipant from "./CurrentParticipant";
import { useTranslate } from '@tolgee/react';
import SelectableParticipant from "./SelectableParticipant";
import {
  useLocalParticipant,
  useRemoteParticipants,
} from "@livekit/components-react";
import { useConversationService } from "../../Context/ConversationsContext";
import { cloneDeep } from "lodash";
import { useCallsService } from "../../Context/CallsContext";
import { getConferenceAudienceApiCall } from "../../API/conferences/conferencesService";

import SpinnerComponent from "../GenericComponents/SpinnerComponent";
import {
  ACCESS_LEVEL,
} from "../../Helpers/subscriptions/SubscriptionsHelper";
import { ACCESS_LEVEL_PLATFORM, SEQUENCIAL_TRANSLATION } from "../../Constants/constantValues";
import { displayUserAvatar } from "../../Helpers/chatHelper/AvatarHelper";
import DraggableList, { useDragHandle } from "../GenericComponents/DraggableList";
import { ReactComponent as DragIcon } from "../../Assets/iconsv2/generic/dragIcon.svg";
import { useAuthService } from "../../Context/AuthContext";
import copy from "copy-to-clipboard";
import { useMeetingsService } from "../../Context/MeetingContext";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { useNotify } from "../../Helpers/Notifications";
import SearchMenu from "../GenericComponents/SearchMenu";
import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import { User } from "lucide-react";

const Participants = ({
  callInfo,
  setLoading,
  activeCallRef,
  setSelectedCall,
  callParticipants,
  hansleParticipantsBtn,
  isConferenceHost = false,
  addObserver,
  removeObserver,
  speakersQueue = [],
  translationType,
  canReorderSpeakers = false,
  onReorderSpeakers,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [audienceList, setAudienceList] = useState([]);
  const isConferenceCall = !!callInfo?.conference_id;
  const { getContactsOnPlatformForCurrentUser, getContacts } = useConversationService();
  const { addCallParticipant, callOfflineParticipant, getCallDetails, muteAllParticipants } = useCallsService();
  const { anonUser, user } = useAuthService();
  const isCurrentUserAdmin = callInfo?.call_admin_ids?.includes(user?.id);
  const localParticipants = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const { notifySuccess, notifyError, notifyInfo } = useNotify();
  const { t } = useTranslate();

  const { currentMeetingRef } = useMeetingsService();
  const { checkIfUserHasPermisionFor } = useAuthService();

  const url = currentMeetingRef?.current?.url;

  const [pendingWaitingOrder, setPendingWaitingOrder] = useState(null);

  useEffect(() => {
    setPendingWaitingOrder(null);
  }, [speakersQueue]);

  const queuedSpeakers =
    translationType === SEQUENCIAL_TRANSLATION
      ? (speakersQueue || [])
          .map((speaker) => callParticipants.find((participant) => String(participant.id) === String(speaker.user_id)))
          .filter(Boolean)
      : [];
  const [currentSpeaker, ...queuedWaitingSpeakers] = queuedSpeakers;
  const waitingSpeakers = pendingWaitingOrder
    ? pendingWaitingOrder
        .map((id) => queuedWaitingSpeakers.find((participant) => participant.id === id))
        .filter(Boolean)
    : queuedWaitingSpeakers;

  const handleReorderWaitingSpeakers = (reordered) => {
    const userIds = reordered.map((participant) => participant.id);
    setPendingWaitingOrder(userIds);
    onReorderSpeakers?.(userIds);
  };

  const renderQueueRow = (participant, position, draggable = false) => (
    <QueueRow>
      <QueuePosition $active={position === 1}>{position}</QueuePosition>
      <QueueAvatar>{displayUserAvatar(participant, true)}</QueueAvatar>
      <QueueName>
        {participant.name}
        {participant.id === user?.id && ` (${t('call_modal.you')})`}
      </QueueName>
      <QueueStatus $active={position === 1}>
        {position === 1
          ? t('call_modal.speaking')
          : position === 2
            ? t('call_modal.next_to_speak')
            : ""}
      </QueueStatus>
      {draggable && <QueueDragHandle />}
    </QueueRow>
  );

  const handleGetContacts = () => {
    setIsLoadingContacts(true);
    getContacts(
      (response) => {
        let allContactsList = response.data.on_app_contacts || [];
        let sortedAppContacts = allContactsList.sort((a, b) =>
          a.name > b.name ? 1 : -1
        );
        setContacts(sortedAppContacts);
        setAllContacts(sortedAppContacts);
        setIsLoadingContacts(false);
      },
      (error) => {
        setIsLoadingContacts(false);

        console.log(error);
      }
    );
  };

  const handleSelectContact = (contact) => {
    let newList = cloneDeep(selectedParticipants);
    let found = newList.find((selected) => selected.id === contact.id);
    if (found) {
      newList = newList.filter((selected) => selected.id !== found.id);
    } else {
      newList.push(contact);
    }
    setSelectedParticipants(newList);
  };

  const isSelected = (participant) => {
    if (selectedParticipants.find((item) => item.id === participant.id)) {
      return true;
    } else {
      return false;
    }
  };

  const handleAddCallParticipant = (participants, call) => {
    let callOtherParticipants = call.other_participants.length;
    let participantsLength = participants.length;

    let totalNumberOfUsers = callOtherParticipants + participantsLength;
    //Offering changes
    let accessLevel 
    if (totalNumberOfUsers < 5) {
      accessLevel = ACCESS_LEVEL.PREMIUM;
    } else {
      accessLevel = ACCESS_LEVEL_PLATFORM;
    }

    checkIfUserHasPermisionFor(
      accessLevel,
      () => {
        setLoading(true);
        let usersId = participants.map((item) => item.id);
        addCallParticipant(
          usersId,
          call.id,
          (response) => {
            setSelectedParticipants([]);
            setSelectedCall(response.data.details);
            activeCallRef.current.details = response.data.details;
            setLoading(false);
          },
          (error) => {
            setLoading(false);
            notifyError(t('general.something_wrong'));
          }
        );
      },
      () => {
        notifyError(t('misc.need_to_upgrade_subscription'));
      },
      () => {
        notifyError(t('misc.need_to_upgrade_subscription'));
      }
    );
  };

  const handleCallOfflineParticipant = (participantId) => {
    callOfflineParticipant(
      participantId,
      callInfo.id,
      (response) => {
        notifySuccess(t('call_modal.notified_user'));
      },
      (error) => {
        notifyError(t('general.something_wrong'));
      }
    );
  };

  const refreshCallDetails = () => {
    getCallDetails(
      callInfo.uuid,
      (response) => {
        setSelectedCall(response.data.details);
        activeCallRef.current.details = response.data.details;
      },
      (error) => {
        console.error('Failed to refresh call details:', error);
      }
    );
  };

  const handleSearch = () => {
    let filteredContacts = cloneDeep(allContacts);
    if (searchValue) {
      filteredContacts = contacts.filter((contact) =>
        contact.name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    setContacts(filteredContacts);
  };

  const filterInvitedParticipants = (contacts) => {
    const filteredContacts = contacts.filter(
      (contact) =>
        !callParticipants.some((participant) => participant.id === contact.id)
    );
    return filteredContacts;
  };

  const handleShareBtn = () => {
    copy(url);
    notifySuccess(t('misc.link_copied'));
  };

  const handleMuteAll = () => {
    muteAllParticipants(
      callInfo.id,
      () => {
        notifySuccess(t('call_modal.all_muted'));
      },
      () => {
        notifyError(t('general.something_wrong'));
      }
    );
  };

  useEffect(() => {
    if (!isConferenceHost || !callInfo?.conference_id) return;

    getConferenceAudienceApiCall(
      callInfo.conference_id,
      (response) => setAudienceList(response.data.audience || []),
      () => {}
    );
  }, [isConferenceHost, callInfo?.conference_id]);

  useEffect(() => {
    if (!isConferenceHost || !addObserver || !removeObserver) return;

    const observer = {
      channelName: "ConferenceCallChannel",
      update: (rawData) => {
        try {
          const msg = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
          if (msg.message_type === "audience_updated") {
            setAudienceList(msg.audience || []);
          }
        } catch {}
      },
    };

    addObserver(observer);
    return () => removeObserver(observer);
  }, [isConferenceHost, addObserver, removeObserver]);

  useEffect(() => {
    if (anonUser.current || isConferenceCall) {
      return;
    }

    handleGetContacts();
  }, [user, isConferenceCall]);

  useEffect(() => {
    handleSearch();
  }, [searchValue]);
  return (
    <Container>
      <ParticipantsWrapper>
        <Line>
          <Title>{t('call_modal.participants')}</Title>
          <CloseButton onClick={hansleParticipantsBtn}>
            <CloseIcon />
          </CloseButton>
        </Line>

        {callInfo.meeting_id && (
          <>
            <Line>
              <Substitle>
                {t('call_modal.share_meeting_link').toUpperCase()}
              </Substitle>
            </Line>
            <div style={{ padding: "0 14px" }}>
              <MeetingLinkWrapper>
                <MeetingLink>{url}</MeetingLink>
                <ShareBtn onClick={handleShareBtn}>
                  {t('meetings.share')}
                </ShareBtn>
              </MeetingLinkWrapper>
            </div>
          </>
        )}
        {queuedSpeakers.length > 0 && (
          <>
            <Line>
              <Substitle>{t('call_modal.speakers_queue').toUpperCase()}</Substitle>
            </Line>
            <CurrentParticipantsList>
              {renderQueueRow(currentSpeaker, 1)}
              <DraggableList
                items={waitingSpeakers}
                onReorder={handleReorderWaitingSpeakers}
                disabled={!canReorderSpeakers || waitingSpeakers.length < 2}
                renderItem={(participant, index) =>
                  renderQueueRow(
                    participant,
                    index + 2,
                    canReorderSpeakers && waitingSpeakers.length > 1
                  )
                }
              />
            </CurrentParticipantsList>
          </>
        )}

        <Line>
          <Substitle>
            {t('call_modal.current_participants').toUpperCase()}
          </Substitle>
          {isCurrentUserAdmin && (
            <MuteAllButton onClick={handleMuteAll}>
              {t('call_modal.mute_all')}
            </MuteAllButton>
          )}
        </Line>
        <CurrentParticipantsList>
          {callParticipants.map((participant) => (
            <CurrentParticipant
              key={participant.id}
              meetingParticipant={callInfo.meeting_id}
              participant={participant}
              callOfflineParticipant={handleCallOfflineParticipant}
              callInfo={callInfo}
              refreshCallDetails={refreshCallDetails}
              isConferenceCall={isConferenceCall}
              online={remoteParticipants.find(
                (p) =>
                  p.identity == participant.id ||
                  localParticipants.localParticipant.identity == participant.id
              )}
            />
          ))}
        </CurrentParticipantsList>

        {isConferenceHost && audienceList.length > 0 && (
          <>
            <Line>
              <Substitle>{t('call_modal.audience').toUpperCase()}</Substitle>
            </Line>
            <CurrentParticipantsList>
              {audienceList.map((attendee) => (
                <AudienceRow key={attendee.id}>
                  <AudienceAvatar online={attendee.online} />
                  <AudienceName>{attendee.name}</AudienceName>
                  {attendee.join_mode === "in_person" && (
                    <InPersonIcon
                      size={12}
                      strokeWidth={2}
                      title={t('call_modal.joined_in_person')}
                    />
                  )}
                  <AudienceStatus offline={!attendee.online}>
                    {attendee.online ? t('general.online') : t('general.offline')}
                  </AudienceStatus>
                </AudienceRow>
              ))}
            </CurrentParticipantsList>
          </>
        )}

        {!anonUser.current && !isConferenceCall && (
          <>
            <Line>
              <Substitle style={{ textTransform: "uppercase" }}>
                {t('call_modal.invite_participants')}
              </Substitle>
            </Line>
            <Line>
              <SearchMenu
                style={{ width: "100%" }}
                value={searchValue}
                placeholder={t('call_modal.search_by_name')}
                setvalue={setSearchValue}
              />
            </Line>
            <CurrentParticipantsList>
              {filterInvitedParticipants(contacts).map((contact) => (
                <SelectableParticipant
                  key={contact.id}
                  participant={contact}
                  handleClick={() => handleSelectContact(contact)}
                  selected={isSelected(contact)}
                />
              ))}
              <SpinnerComponent
                isLoading={isLoadingContacts}
              ></SpinnerComponent>
            </CurrentParticipantsList>
          </>
        )}
      </ParticipantsWrapper>

      <Bottom>
        {!isConferenceCall && selectedParticipants?.length > 0 && (
          <PrimaryButton
            style={{ padding: "16px", width: "100%" }}
            onClick={() =>
              handleAddCallParticipant(selectedParticipants, callInfo)
            }
          >
            {t('call_modal.invite')}
          </PrimaryButton>
        )}
      </Bottom>
    </Container>
  );
};

export default Participants;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 300px;
  max-width: 400px;

  *::-webkit-scrollbar-track {
    background: rgba(26, 29, 36, 0); /* Track background */
  }

  *::-webkit-scrollbar-thumb {
    background: rgba(26, 29, 36, 0); /* Track background */
  }
`;
const ParticipantsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  padding: 20px 10px;
  border-radius: 4px;
  overflow: auto;
  gap: 20px;
`;

const Bottom = styled.div`
  display: flex;
  width: 100%;
  min-height: 80px;
  padding: 18px 0 10px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const Line = styled.div`
  display: flex;
  padding: 5px 16px;
  align-items: center;
  width: 100%;
`;

const Substitle = styled.p`
  margin-top: 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const CurrentParticipantsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SearchBox = styled.div`
  background: rgba(118, 118, 128, 0.24);
  display: flex;
  padding: 14px;
  gap: 10px;
  width: 100%;
  border-radius: 4px;
  input {
    width: 100%;
    background: none;
    border: none;
    outline: none;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 14px;
    line-height: 16px;
  }
`;

const MeetingLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 14px;
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  border-radius: 4px;
  gap: 15px;
`;
const MeetingLink = styled.p`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  line-height: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ShareBtn = styled.button`
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  border: none;
  cursor: pointer;
  border-radius: 4px;
  color: ${({ theme }) => theme.primaryButton.color};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`;

const MuteAllButton = styled.button`
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.primaryButton.color};
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  margin-left:auto;
  svg{
height:28px;
width:28px;
color:${({ theme }) => theme.menu.selectedOpionBackground};
stroke:${({ theme }) => theme.sidebar.borderColor};
}
`;

const AudienceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 16px;
`;

const AudienceAvatar = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ online, theme }) =>
    online ? "#34C759" : theme.menu.secondaryTextColor};
`;

const AudienceName = styled.span`
  flex: 1;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const AudienceStatus = styled.span`
  font-size: 12px;
  line-height: 14px;
  color: ${({ offline, theme }) =>
    offline ? theme.menu.secondaryTextColor : "#34C759"};
`;

const QueueDragHandle = () => {
  const dragHandleProps = useDragHandle();
  return (
    <DragHandleWrapper {...dragHandleProps}>
      <DragIcon width={16} height={16} />
    </DragHandleWrapper>
  );
};

const DragHandleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: grab;
  touch-action: none;
  color: ${({ theme }) => theme.menu.secondaryTextColor};

  &:active {
    cursor: grabbing;
  }
`;

const QueueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  min-height: 36px;
`;

const QueuePosition = styled.span`
  width: 16px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: ${({ $active, theme }) =>
    $active ? theme.primaryButton.color : theme.menu.secondaryTextColor};
`;

const QueueAvatar = styled.div`
  display: flex;
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 100%;
  overflow: hidden;
`;

const QueueName = styled.span`
  flex: 1;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const QueueStatus = styled.span`
  font-size: 12px;
  line-height: 14px;
  color: ${({ $active, theme }) =>
    $active ? theme.primaryButton.color : theme.menu.secondaryTextColor};
`;

const InPersonIcon = styled(User)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;
